import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import Table from "../../../../applicationUi/components/Table";
import { format } from "date-fns";
import { fetchQuickBooksPaymentsReport } from "../../../../store/thunks/reportsThunk";
import * as XLSX from "xlsx";
import { toast } from 'react-toastify';

// Currency formatting utility
const formatCurrency = (amount) => {
  if (amount == null) return "$0.00";
  return `$${Number(amount).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}`;
};

const QuickBooksPaymentsReport = () => {
  const dispatch = useDispatch();
  const [statusFilter, setStatusFilter] = useState("all");

  const {
    data: qboPaymentsData,
    loading,
    error,
  } = useSelector((state) => state.reports.quickBooksPayments || { data: null, loading: false, error: null });

  const data = qboPaymentsData?.data || {};
  const summary = data.summary || {};
  const payments = data.payments || [];

  // Debug: Log current state and full reports state  
  console.log('[QuickBooks Payments] Current state:', { qboPaymentsData, loading, error });
  const fullReportsState = useSelector((state) => state.reports);
  console.log('[QuickBooks Payments] Full reports state:', fullReportsState);

  // Load data when component mounts and when statusFilter changes
  useEffect(() => {
    console.log('[QuickBooks Payments] Loading data with statusFilter:', statusFilter);
    dispatch(fetchQuickBooksPaymentsReport({ statusFilter }));
  }, [dispatch, statusFilter]);

  const handleDownload = () => {
    if (payments.length === 0) {
      toast.warning("No data available to download.");
      return;
    }

    const data = payments.map((payment) => ({
      "Payment ID": payment._id,
      "Client": payment.clientName || "Unknown",
      "Payment Method": payment.paymentMethod,
      "Amount": Number(payment.amount || 0).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2}),
      "Payment Date": format(new Date(payment.paymentDate), "yyyy-MM-dd"),
      "Is Deposit": payment.deposit ? "Yes" : "No",
      "QBO Status": payment.QBOPostingStatus,
      "QBO Payment ID": payment.QBOPaymentId || "Not Posted",
      "Last Attempt": payment.QBOLastAttemptedAt ? format(new Date(payment.QBOLastAttemptedAt), "yyyy-MM-dd HH:mm") : "Never",
      "Posted At": payment.QBOPostedAt ? format(new Date(payment.QBOPostedAt), "yyyy-MM-dd HH:mm") : "Not Posted",
      "Error": payment.QBOPostingError || "None",
      "Invoices": payment.invoiceIds ? payment.invoiceIds.join(", ") : "None"
    }));

    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "QuickBooks Payments");
    XLSX.writeFile(workbook, `QuickBooks_Payments_Report_${format(new Date(), "yyyy-MM-dd")}.xlsx`);
  };

  const paymentColumns = [
    { header: "Payment ID", accessor: (row) => row._id.slice(-8) },
    { header: "Client", accessor: "clientName" },
    { header: "Payment Method", accessor: "paymentMethod" },
    { header: "Amount", accessor: (row) => formatCurrency(row.amount) },
    { header: "Payment Date", accessor: (row) => format(new Date(row.paymentDate), "MMM dd, yyyy") },
    { 
      header: "Type", 
      accessor: (row) => (
        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
          row.deposit ? "bg-blue-100 text-blue-800" : "bg-gray-100 text-gray-800"
        }`}>
          {row.deposit ? "Deposit" : "Payment"}
        </span>
      )
    },
    { 
      header: "QBO Status", 
      accessor: (row) => (
        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
          row.QBOPostingStatus === "success" ? "bg-green-100 text-green-800" :
          row.QBOPostingStatus === "error" ? "bg-red-100 text-red-800" :
          "bg-yellow-100 text-yellow-800"
        }`}>
          {row.QBOPostingStatus}
        </span>
      )
    },
    { header: "QBO Payment ID", accessor: (row) => row.QBOPaymentId || "Not Posted" },
    { 
      header: "Last Attempt", 
      accessor: (row) => row.QBOLastAttemptedAt ? format(new Date(row.QBOLastAttemptedAt), "MMM dd, yyyy HH:mm") : "Never"
    },
    { 
      header: "Error", 
      accessor: (row) => row.QBOPostingError ? (
        <div className="max-w-xs">
          <span className="text-red-600 text-sm truncate block" title={row.QBOPostingError}>
            {row.QBOPostingError}
          </span>
        </div>
      ) : "None"
    },
    { 
      header: "Invoices", 
      accessor: (row) => row.invoiceIds && row.invoiceIds.length > 0 ? (
        <div className="max-w-xs">
          <span className="text-sm text-gray-600 truncate block" title={row.invoiceIds.join(", ")}>
            {row.invoiceIds.length} invoice{row.invoiceIds.length !== 1 ? 's' : ''}
          </span>
        </div>
      ) : "None"
    }
  ];

  if (error) return <div className="text-red-500 p-4">Error: {error}</div>;

  return (
    <div className="space-y-6">
      {/* Filter and Summary */}
      <div className="bg-white p-6 rounded-sm shadow-sm">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">QuickBooks Payments Report</h3>
            <p className="text-gray-600">Monitor payment posting status to QuickBooks Online</p>
          </div>
          <div className="flex gap-4">
            <div>
              <label htmlFor="statusFilter" className="block text-sm font-medium text-gray-700 mb-1">
                Filter by Status
              </label>
              <select
                id="statusFilter"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="block w-full rounded-sm border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              >
                <option value="all">All Payments</option>
                <option value="pending">Pending</option>
                <option value="error">Errors Only</option>
                <option value="success">Successfully Posted</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Show loading state */}
      {loading && (
        <div className="flex justify-center items-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
            <p className="mt-2 text-gray-600">Loading QuickBooks payment data...</p>
          </div>
        </div>
      )}

      {/* Show data only if we have it and not loading */}
      {!loading && data && Object.keys(summary).length > 0 && (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <div className="bg-white p-6 rounded-sm shadow-sm">
              <h4 className="text-sm font-medium text-gray-500">Total Payments</h4>
              <p className="text-2xl font-bold text-gray-900">{summary.totalPayments || 0}</p>
            </div>
            <div className="bg-white p-6 rounded-sm shadow-sm">
              <h4 className="text-sm font-medium text-gray-500">Successfully Posted</h4>
              <p className="text-2xl font-bold text-green-600">{summary.successfulPosts || 0}</p>
            </div>
            <div className="bg-white p-6 rounded-sm shadow-sm">
              <h4 className="text-sm font-medium text-gray-500">Pending</h4>
              <p className="text-2xl font-bold text-yellow-600">{summary.pendingPosts || 0}</p>
            </div>
            <div className="bg-white p-6 rounded-sm shadow-sm">
              <h4 className="text-sm font-medium text-gray-500">Errors</h4>
              <p className="text-2xl font-bold text-red-600">{summary.errorPosts || 0}</p>
            </div>
            <div className="bg-white p-6 rounded-sm shadow-sm">
              <h4 className="text-sm font-medium text-gray-500">Deposits</h4>
              <p className="text-2xl font-bold text-blue-600">{summary.depositPayments || 0}</p>
            </div>
          </div>

          {/* Payment Table */}
          <div className="bg-white shadow-sm rounded-sm">
            <div className="p-6">
              <Table
                columns={paymentColumns}
                data={payments}
                title="Payment Posting Status"
                description="Detailed view of payment posting status to QuickBooks Online"
                actions={[
                  {
                    label: "Download Excel",
                    onClick: handleDownload,
                  },
                ]}
              />
            </div>
          </div>

          {/* Error Summary */}
          {summary.errorPosts > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-sm p-6">
              <h3 className="text-lg font-medium text-red-900 mb-2">Posting Errors Summary</h3>
              <p className="text-red-700 mb-4">
                {summary.errorPosts} payment{summary.errorPosts !== 1 ? 's' : ''} failed to post to QuickBooks. 
                Review the error details above and contact your system administrator if needed.
              </p>
              {summary.commonErrors && summary.commonErrors.length > 0 && (
                <div>
                  <h4 className="font-medium text-red-900 mb-2">Common Error Types:</h4>
                  <ul className="list-disc list-inside text-red-700 space-y-1">
                    {summary.commonErrors.map((error, index) => (
                      <li key={index}>{error.error} ({error.count} occurrence{error.count !== 1 ? 's' : ''})</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* Show message if no data and not loading */}
      {!loading && (!data || Object.keys(summary).length === 0) && (
        <div className="bg-white p-6 rounded-sm shadow-sm text-center">
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Data Available</h3>
          <p className="text-gray-600">
            No payments found for the selected filter. Try adjusting your filter settings.
          </p>
        </div>
      )}
    </div>
  );
};

export default QuickBooksPaymentsReport;